<script>
	/* eslint-disable svelte/no-at-html-tags */
	// Constants
	import { TwoFactorTypes } from '@/Constants/User.js';
	// Utils
	import { notify } from '@/Services/Notify.js';
	import { objectWithKeys } from '@/Services/Helpers.js';
	// Components
	import TextInput from '@/Components/TextInput.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	// Models
	import TwoFactor from '@/Models/TwoFactor.js';

	export let data;

	const twoFactorTypesList = objectWithKeys(TwoFactorTypes, ['SMS', 'Email', 'TOTP']);

	let loadingEnroll = false;
	let loadingEnable = false;
	let loadingDisable = false;
	let loadingRecoveryCodes = false;
	let showRecoveryCodes = false;
	let enrolling = false;
	let TwoFactorModel = new TwoFactor();

	$: enrollData = {
		two_factor_type: null
	};
	$: challengeData = {
		code: ''
	};
	$: recoveryCodes = [];
	$: enrolledInTwoFactor = data && data.two_factor_type != null;
	$: currentTwoFactorType = data && enrolledInTwoFactor && getTwoFactorType(data.two_factor_type);
	$: qrCode = null;
	$: errors = null;

	function getTwoFactorType(twoFactorType) {
		for (const [twoFactorTypeName, twoFactorTypeIndex] of Object.entries(twoFactorTypesList)) {
			if (twoFactorTypeIndex === twoFactorType) {
				return twoFactorTypeName;
			}
		}
		return null;
	}

	//fire off request to issue token / generate secret
	function beginTwoFactorEnrollment() {
		//check if the phone is set for sms
		if (enrollData.two_factor_type === TwoFactorTypes.SMS) {
			if (data.phone === '' || data.country === '') {
				//you need a valid phone number for that!
				notify({
					type: 'danger',
					title: 'Error!',
					message:
						'You need a valid phone number and country set on your profile before you can enable SMS two factor.',
					timeout: 3
				});
				return false;
			}
		}
		loadingEnroll = true;
		TwoFactorModel.enroll(enrollData)
			.then(() => {
				enrolling = true;
				if (enrollData.two_factor_type === TwoFactorTypes.TOTP) {
					getQrCode();
				}
			})
			.catch((error) => {
				errors = error;
			})
			.finally(() => {
				loadingEnroll = false;
			});
	}

	function getQrCode() {
		TwoFactorModel.qrCode()
			.then((results) => {
				qrCode = results.data;
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'An error occurred getting your QR code',
					timeout: 5
				});
			});
	}

	//send the response to activate 2fa
	function enableTwoFactor() {
		loadingEnable = true;
		TwoFactorModel.enable(challengeData)
			.then((results) => {
				recoveryCodes = [];
				notify({
					type: 'success',
					title: 'Two-factor authentication!',
					message: results.data.meta.message,
					timeout: 3
				});
				enrolling = false;
				data.two_factor_type = enrollData.two_factor_type;
				regenerateRecoveryCodes();
			})
			.catch((error) => {
				errors = error;
			})
			.finally(() => {
				loadingEnable = false;
			});
	}

	//remove 2fa (should we confirm password before this?)
	function disableTwoFactor() {
		loadingDisable = true;
		TwoFactorModel.disable()
			.then((results) => {
				notify({
					type: 'success',
					title: 'Two-factor authentication!',
					message: results.data.meta.message,
					timeout: 3
				});
				data.two_factor_type = null;
			})
			.catch((error) => {
				errors = error;
			})
			.finally(() => {
				loadingDisable = false;
			});
	}

	//generate new recovery codes (done within modal)
	function regenerateRecoveryCodes() {
		loadingRecoveryCodes = true;
		TwoFactorModel.regenerateRecoveryCodes()
			.then((results) => {
				notify({
					type: 'success',
					title: 'Two-factor recovery codes!',
					message: results.data.meta.message,
					timeout: 3
				});
				recoveryCodes = results.data.meta.recovery_codes;
				showRecoveryCodes = true;
			})
			.catch((error) => {
				errors = error;
			})
			.finally(() => {
				loadingRecoveryCodes = false;
			});
	}
</script>

<div class="md:mx-2 w-full md:w-1/3 rounded-b bg-gray-100">
	<!-- Header -->
	<div class="flex items-center px-2 bg-dark-grey text-light rounded-t">
		<h2 class="flex-1 py-2 text-xl italic font-sans-condensed">Two-factor details</h2>
	</div>
	<!-- /Header -->
	<!-- Section body -->
	<div class="mt-2">
		<div class="flex flex-wrap px-6 py-2">
			<p class="pt-4 pb-8 w-full">
				You <span class:text-danger={!enrolledInTwoFactor} class:text-secondary={enrolledInTwoFactor}
					>{enrolledInTwoFactor ? 'ARE' : 'ARE NOT'}</span
				>
				enrolled in{enrolledInTwoFactor ? ' ' + currentTwoFactorType : ''} two factor authentication.
			</p>
			{#if enrolledInTwoFactor}
				<LoadingButton
					on:click={() => disableTwoFactor()}
					loading={loadingDisable}
					className="px-4 py-2 text-sm font-bold text-white whitespace-nowrap rounded focus:outline-none bg-danger hover:bg-dark mr-8"
				>
					Disable Two Factor
				</LoadingButton>
				<LoadingButton
					on:click={() => regenerateRecoveryCodes()}
					loading={loadingRecoveryCodes}
					className="px-4 py-2 text-sm font-bold text-white whitespace-nowrap rounded focus:outline-none bg-secondary hover:bg-dark"
				>
					Regenerate recovery codes
				</LoadingButton>
				{#if showRecoveryCodes}
					<div class="w-full border-t-2 my-4" />
					<h3 class="font-bold mb-4 w-full">Your recovery codes!</h3>
					<br />
					<p class="mb-2 w-full">Please make note of these as they will only be shown once!</p>
					{#each recoveryCodes as recoveryCode}
						<div class="flex my-2 text-xl justify-center w-full">{recoveryCode}</div>
					{/each}
				{/if}
			{:else}
				<div class="w-full border-t-2 mb-4" />
				<h3 class="font-bold mb-4">Enroll in two factor authentication</h3>
				{#if !enrolling}
					<SelectInput
						className="pb-4 w-full"
						label="Two Factor Mode"
						name="profile_two_factor_type"
						disabled={loadingEnroll}
						bind:value={enrollData.two_factor_type}
					>
						<option value={null}>Please select two factor method</option>
						{#each Object.entries(twoFactorTypesList) as TwoFactorType}
							{#if TwoFactorType[1] == TwoFactorTypes.Email}
								<option value={TwoFactorType[1]} disabled>{TwoFactorType[0]} [Please use SMS or TOTP]</option>
							{:else}
								<option value={TwoFactorType[1]}>{TwoFactorType[0]}</option>
							{/if}
						{/each}
					</SelectInput>
					{#if !(enrollData.two_factor_type === null)}
						<LoadingButton
							on:click={() => beginTwoFactorEnrollment()}
							loading={loadingEnroll}
							className="px-4 py-2 text-sm font-bold text-white whitespace-nowrap rounded focus:outline-none bg-secondary hover:bg-dark"
						>
							Enroll
						</LoadingButton>
					{/if}
				{:else}
					{#if enrollData.two_factor_type === 1 || enrollData.two_factor_type === 2}
						<p>
							We've issued you a OTP (one-time passcode) like you will be issued when you login in the future. Once
							you've received this code please enter it below to enable two factor authentication
						</p>
					{:else}
						<p>
							Using the QR code below, add a new account to your TOTP (time-based one-time passcode) app of your choice
							(we suggest authy!). Once added please enter the code presented to enable two factor authentication
						</p>
						{#if qrCode != null}
							<div class="w-1/3 mx-auto mt-4 items-center justify-center text-center">
								{@html qrCode}
							</div>
						{/if}
					{/if}

					<TextInput
						className="pb-4 w-full mt-6"
						label="Enter your two factor code"
						name="challenge_code"
						type="text"
						required="true"
						disabled={loadingEnable}
						bind:value={challengeData.code}
					/>

					<LoadingButton
						on:click={() => enableTwoFactor()}
						loading={loadingEnable}
						className="px-4 py-2 text-sm font-bold text-white whitespace-nowrap rounded focus:outline-none bg-secondary hover:bg-dark"
					>
						Enable two factor authentication
					</LoadingButton>
				{/if}
			{/if}
			{#if errors && errors.error}
				<div class="w-full">
					<div class="mb-3 text-center form-error">{errors.error}</div>
				</div>
			{/if}
		</div>
	</div>
	<!-- Section body -->
</div>
